import React, { useMemo, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import Select from "react-select"
import { Button } from "reactstrap"
import { FaClipboardList, FaUsers } from "react-icons/fa"

import TableContainer from "../../../components/Common/DataTableContainer"
import { getBazaarInterestList, combineGroupBazaarList, downloadBazaarInterestList } from "../../../redux/actions/pvs"
import { DOWNLOAD_BAZAAR_INTEREST_LIST_RESET } from "../../../redux/types/pvs"
import StatsCard from "components/StatsCard"
import PageLoader from "components/Common/PageLoader"
import { toast } from "react-toastify"

const BazaarInterestList = () => {
  document.title = "PULA Bazaar Interest List"

  const dispatch = useDispatch()
  const history = useNavigate()
  const bazaarInterestList = useSelector(state => state.pvs.bazaarInterestList)
  const combineGroupBazaarListData = useSelector(state => state.pvs.combineGroupBazaarList)

  const [selectedBazaar, setSelectedBazaar] = useState(null)
  const [bazaarOptions, setBazaarOptions] = useState([])

  const bazaarInterestFileLink = useSelector(state => state.pvs.bazaarInterestFileLink)
  const downloadBazaarInterestScreen = useSelector(
    state => state.screens.pvs.downloadBazaarInterestList
  )

  useEffect(() => {
    dispatch(combineGroupBazaarList({}))
  }, [])

  useEffect(() => {
    if (downloadBazaarInterestScreen.success && bazaarInterestFileLink) {
      const link = document.createElement("a")
      link.href = bazaarInterestFileLink
      link.download = "bazaar-interest-list.xlsx"
      link.click()
      dispatch({ type: DOWNLOAD_BAZAAR_INTEREST_LIST_RESET })
    }
    if (downloadBazaarInterestScreen.error) {
      toast.error(downloadBazaarInterestScreen.error)
      dispatch({ type: DOWNLOAD_BAZAAR_INTEREST_LIST_RESET })
    }
  }, [downloadBazaarInterestScreen])

  useEffect(() => {
    dispatch(getBazaarInterestList({ enc_combine_group_id: selectedBazaar ? selectedBazaar.value : undefined }))
  }, [selectedBazaar])

  useEffect(() => {
    if (combineGroupBazaarListData) {
      const options = [...combineGroupBazaarListData]
        .sort((a, b) => b.combine_group_id - a.combine_group_id)
        .map(item => ({ label: item.bazaar_name, value: item.enc_combine_group_id }))
      setBazaarOptions(options)
      if (!selectedBazaar && options.length > 0) {
        setSelectedBazaar(options[0])
      }
    }
  }, [combineGroupBazaarListData])

  const totalInterestForms = (bazaarInterestList || []).length

  const totalAttendees = (bazaarInterestList || []).reduce((sum, item) => {
    const val = Number(item.people_count_for_booking)
    return isNaN(val) ? sum : sum + val
  }, 0)

  const handleSearch = () => {
    dispatch(getBazaarInterestList({ enc_combine_group_id: selectedBazaar ? selectedBazaar.value : undefined }))
  }

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "index",
        enableColumnFilter: false,
        enableSorting: false,
        cell: info => info.row.index + 1,
      },
      {
        header: "Full Name",
        accessorKey: "full_name",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "Contact Number",
        accessorKey: "contact_number",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "PVS Card",
        accessorKey: "pvs_card_number",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "Participated Before",
        accessorKey: "participated_earlier",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Bazaar",
        accessorKey: "bazaar_season_interest",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Coming on Booking Day",
        accessorKey: "booking_visit_status",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Attendees",
        accessorKey: "people_count_for_booking",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Submitted On",
        accessorKey: "created_at",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const val = info.getValue()
          return val ? new Date(val).toLocaleDateString() : "-"
        },
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: info => {
          const row = info.row.original
          return (
            <div>
              {row.enc_pvs_entry_id && (
                <Button
                  size="md"
                  color="primary"
                  className="w-20 text-transform"
                  onClick={() =>
                    history(`/pvs-details/${row.enc_pvs_entry_id}`)
                  }
                >
                  View PVS Details
                </Button>
              )}
            </div>
          )
        },
      },
    ],
    []
  )

  return (
    <>
    <div className="page-content">
      <div className="container-fluid">

        <div className="mt-3 mb-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h4 className="h4 mb-0 nom-title">Bazaar Interest List</h4>
            <Button
              color="primary"
              onClick={() =>
                dispatch(
                  downloadBazaarInterestList({
                    enc_combine_group_id: selectedBazaar ? selectedBazaar.value : undefined,
                  })
                )
              }
              disabled={downloadBazaarInterestScreen.loading}
            >
              {downloadBazaarInterestScreen.loading ? "Downloading..." : "Download Report"}
            </Button>
          </div>
          <div className="d-flex align-items-center gap-2 w-100">
            <div style={{ width: "70%" }}>
              <Select
                options={bazaarOptions}
                value={selectedBazaar}
                onChange={setSelectedBazaar}
                classNamePrefix="select"
                placeholder="Select Bazaar..."
                isClearable
              />
            </div>
            <div style={{ width: "30%" }}>
              <Button color="primary" onClick={handleSearch} className="w-100">
                Search
              </Button>
            </div>
          </div>
        </div>

        <div className="d-flex gap-3 mb-4">
          <div style={{ flex: 1 }}>
            <StatsCard
              title="Total Interest Forms"
              value={totalInterestForms}
              icon={<FaClipboardList />}
              bgClass="bg-primary-soft"
            />
          </div>
          <div style={{ flex: 1 }}>
            <StatsCard
              title="Total Attendees"
              value={totalAttendees}
              icon={<FaUsers />}
              bgClass="bg-success-soft"
            />
          </div>
        </div>

        <TableContainer
          defaultPageSize={20}
          columns={columns}
          data={bazaarInterestList || []}
          isGlobalFilter={false}
          isPagination={true}
          isCustomPageSize={false}
          SearchPlaceholder="Search records..."
          pagination="pagination"
          isAddButton={false}
          buttonClass="btn btn-primary"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          theadClass="table-light"
          divClassName="table-responsive mb-4 nominee-tbl-wrapper"
        />
      </div>
    </div>
    <PageLoader open={downloadBazaarInterestScreen.loading} />
    </>
  )
}

export default BazaarInterestList
